import React, { useRef, useState } from 'react';
import './action-realworld-list-table.styles.scss';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { faArrowRight, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { format } from 'date-fns';
import { properCase } from '@/utils/properCase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserIcon from '@/components/layout-auth/user-icon/user-icon.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import useAnalytics from '@/utils/useAnalytics';

const RealworldListTable = ({
  gameData,
  gameElements,
  actionItemsToDisplay,
  playerList,
  goToTransaction,
  changeActionItemStatus,
  view,
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const membership = useSelector(state => state.userData.membership);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [toggled, setToggled] = useState({});
  const { trackEvent, trackException } = useAnalytics();
  const approveRef = useRef(null);
  const countRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();

  async function toggleDistributed(actionItemId) {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    console.log('toggling distributed for ', actionItemId);
    let actionItem = actionItemsToDisplay.filter(item => item.id === actionItemId)[0];
    if (!actionItem) {
      actionItem = actionItemsToDisplay[actionItemId];
    }
    if (!actionItem) return;
    const status = actionItem.status === 'pending' ? 'approved' : 'pending';
    if (status === 'pending') {
      setConfirmMessage('Are you sure you want to mark this item as <b>NOT DISTRIBUTED</b>?')
      approveRef.current = () => {
        finishToggle();
      }
    } else {
      finishToggle();
    }

    async function finishToggle() {
      setToggled(prev => ({ ...prev, [actionItem.id]: !prev[actionItem.id] }));
      const res = await UseCloudFunction(
        'toggleDistributedRealWorldItem',
        {
          'gameId': gameData.gameId,
          'actionItem': actionItem,
          'ts': new Date().getTime(),
          'status': status,
          'appId': userProfile.appId,
          'approver_name': `${userProfile.fName} ${userProfile.lName}`
        }
      )
      console.log(res);
      if (res.error) {
        console.error('Error toggling distributed status:', res.error);
        setAlertMessage(`Something went wrong. Please try again later. (Error: ${res.error})`);
        trackException(`Toggle Distributed RealWorld Item Error: ${res.error}`);
        return;
      }
      trackEvent(
        `realworld_action_item_${status}`,
        'Game Manager',
        `RealWorld Action Item ${actionItem.id} for player ${actionItem.playerId} was marked as ${status}.`,
        null,
        membership
      )
      changeActionItemStatus(actionItem.id, status);
    }

  }

  return (
    <div className={`action-realworld-list-table ${screenSize}`}>
      <table className='g-table'>
        <thead>
          <tr>
            <th></th>
            <th></th>
            <th>Player</th>
            {
              (screenSize !== 'mobile' && screenSize !== 'tablet') &&
              <th>Info</th>
            }
            <th>Element</th>
            {
              (screenSize !== 'mobile' && screenSize !== 'tablet') &&
              <>
                <th>Date</th>
                <th>Event</th>
              </>
            }
            <th className="td-center">
              {
                screenSize === 'mobile' || screenSize === 'tablet'
                  ? 'Qty'
                  : 'Quantity'
              }
            </th>
            <th className="td-center">
              {
                screenSize === 'mobile' || screenSize === 'tablet'
                  ? 'Dist'
                  : 'Distributed'
              }
            </th>
          </tr>
        </thead>
        <tbody>
          {
            (
              actionItemsToDisplay &&
              playerList &&
              gameElements &&
              Object.values(actionItemsToDisplay).length > 0
            ) &&
            Object.values(actionItemsToDisplay).map((item, index) => {
              if (index === 0 || countRef.current === 0) {
                countRef.current = 0;
              }
              if (!item.quantity) return null;
              const player = playerList[item.playerId];
              if (!player) return null;
              const element = gameElements[item.elementId];
              if (!element) return null;

              countRef.current++;
              return (
                <tr
                  key={item.id}
                  id={item.id + '-row'}
                  className='tr-container'>
                  <td>
                    <div className={`${item.id}-content`}>
                      {countRef.current}
                    </div>
                  </td>
                  <td >
                    <div className={`${item.id}-content icon`}>
                      <UserIcon
                        profile={player}
                        small={screenSize !== 'mobile' && screenSize !== 'tablet'}
                        tiny={screenSize === 'mobile' || screenSize === 'tablet'}
                      />
                    </div>
                  </td>
                  <td>
                    <div className={`${item.id}-content`}>
                      {player.lName}, {player.fName}
                    </div>
                  </td>

                  {
                    (screenSize !== 'mobile' && screenSize !== 'tablet') &&
                    <td>
                      <div className={`${item.id}-content`}>
                        {player.info}
                      </div>
                    </td>
                  }

                  <td>
                    <div className={`${item.id}-content element`}>
                      {
                        (screenSize !== 'mobile' && screenSize !== 'tablet') &&
                        <div className='icon'>
                          <ImageContainer
                            src={element.icon}
                            alt={`${element.name} icon`} />
                        </div>
                      }
                      {element.name}
                    </div>
                  </td>

                  {
                    (screenSize !== 'mobile' && screenSize !== 'tablet') &&
                    <>
                      <td>
                        <div className={`${item.id}-content`}>
                          {format(new Date(item.ts_submitted), "Pp")}
                        </div>
                      </td>
                      <td>
                        <div className={`${item.id}-content`}>
                          {properCase(item.event)}
                        </div>
                      </td>
                    </>
                  }
                  <td>
                    <div className={`${item.id}-content center`}>
                      {item.quantity}
                    </div>
                  </td>

                  <td>
                    <div className={`${item.id}-content center`}>
                      <button
                        className='g-button text-only'
                        onClick={() => (view === 'completed' || !toggled[item.id])
                          ? toggleDistributed(item.id)
                          : null}
                      >
                        {
                          (item.status === 'pending' && !toggled[item.id])
                            ?
                            <FontAwesomeIcon
                              icon={faSquare}
                              size='xl'
                            />
                            :
                            <FontAwesomeIcon
                              icon={faCheckSquare}
                              size='xl'
                              className='green'
                            />
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })
          }
        </tbody>
      </table>
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
    </div>
  )
}

export default RealworldListTable